import * as React from 'react';
import { AccentDialog } from '../../dialogs/AccentDialog';
import { AccentButton } from '../AccentButton';
import { AccentSpinner } from '../AccentSpinner';
import { AccentCheckUnbound } from '../unbound/AccentCheckUnbound';
import { AccentTextUnbound } from '../unbound/AccentTextUnBound';
import { AccentTextAreaUnbound } from '../unbound/AccentTextAreaUnbound';
import { AccentHtmlUnbound } from '../unbound/AccentHtmlUnbound';
import { AiWandButton } from '../ai/AiWandButton';
import { accentUtils, t } from '../../services/HelperService';
import {
  describeCustomizationAsset,
  fetchCustomizationAssetThumbnail,
  getCustomizationAssetUsedBy,
  isFrameworkError,
  saveCustomizationAssetText,
  suggestCustomizationAssetKey,
  updateCustomizationAsset,
  uploadCustomizationAsset,
} from './customizationApi';

/** The upload types the server accepts (PdfDocumentAssetLimits: png / jpeg / svg up to 5 MB and 4000 px, pdf up to 10 MB and 50 pages, spreadsheets up to 10 MB, text files up to 64 KB). */
export const ASSET_FILE_ACCEPT = '.png,.jpg,.jpeg,.svg,.pdf,.xlsx,.xlsm,.xls,.txt,.md,.json,.yaml,.yml,.csv';
/** Text-file extensions the server stores as text assets (PdfDocumentAssetLimits.TextContentTypesByExtension). */
const TEXT_FILE_PATTERN = /\.(txt|md|json|yaml|yml|csv)$/i;
/** Spreadsheet extensions the server stores as table assets (PdfDocumentAssetLimits.TableContentTypesByExtension). */
const TABLE_FILE_PATTERN = /\.(xlsx|xlsm|xls)$/i;

/** The library kind a chosen file becomes ('pdf' | 'text' | 'table' | 'image'), mirroring PdfDocumentAssetLimits.KindForFilename. */
export const kindForFileName = (name, type = '') => {
  if (/\.pdf$/i.test(name ?? '') || type === 'application/pdf') return 'pdf';
  if (TEXT_FILE_PATTERN.test(name ?? '')) return 'text';
  if (TABLE_FILE_PATTERN.test(name ?? '')) return 'table';
  return 'image';
};

/** The purposes a customisation may attach an asset with (CustomizationDependentAssets.Purposes). */
export const ASSET_PURPOSE_REFERENCE = 'reference';
export const ASSET_PURPOSE_RUNTIME = 'runtime';
const KEY_PATTERN = /^[a-z0-9][a-z0-9-]*$/;
const MAX_KEY_LENGTH = 50;
const MAX_TEXT_CHARS = 64 * 1024;

/** The same slug the server derives (PdfDocumentAssetKey.Derive), for an instant pre-fill before the server's unique suggestion arrives. */
export const deriveAssetKey = (fileName) => {
  const name = (fileName ?? '').toString();
  const slash = Math.max(name.lastIndexOf('/'), name.lastIndexOf('\\'));
  let stem = slash >= 0 ? name.slice(slash + 1) : name;
  const dot = stem.lastIndexOf('.');
  if (dot > 0) stem = stem.slice(0, dot);
  let slug = stem.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  if (slug.length > MAX_KEY_LENGTH) slug = slug.slice(0, MAX_KEY_LENGTH).replace(/-+$/g, '');
  return slug;
};

const keyError = (key) => {
  const k = (key ?? '').toString();
  if (!k) return t('A key is required.');
  if (k.length > MAX_KEY_LENGTH) return t('An asset key may be at most {{max}} characters.', { max: MAX_KEY_LENGTH });
  if (!KEY_PATTERN.test(k)) return t('Keys are lower-case letters, digits and hyphens, starting with a letter or digit (e.g. company-logo).');
  return null;
};

export const formatAssetSize = (bytes) => {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10 * 1024 ? 1 : 0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

/** "1200 × 400 px", "3 pages", "2.1 KB text" — the asset's measure line for lists and the dialog. */
export const describeAssetMeasure = (asset) => {
  if (!asset) return '';
  const parts = [];
  if (asset.Kind === 'pdf' && asset.PageCount) parts.push(`${asset.PageCount} ${asset.PageCount === 1 ? t('page') : t('pages')}`);
  if (asset.Kind === 'table' && asset.PageCount) parts.push(`${asset.PageCount} ${asset.PageCount === 1 ? t('sheet') : t('sheets')}`);
  if (asset.Kind === 'image' && asset.Width && asset.Height) parts.push(`${asset.Width} × ${asset.Height} px`);
  if (asset.SizeBytes) parts.push(formatAssetSize(asset.SizeBytes));
  return parts.join(' · ');
};

const kindLabel = (kind) => {
  switch ((kind ?? '').toString()) {
    case 'text': return t('Text');
    case 'image': return t('Image');
    case 'pdf': return t('PDF');
    case 'table': return t('Spreadsheet');
    default: return kind ?? '';
  }
};

/**
 * Create / edit dialog for one asset of the pdfscript library (Customizations > Assets).
 *
 * mode: 'upload' (new image / pdf / text file from a file), 'any' (the same, opened from an AI session's "Add to
 * library": the file picker also takes text files, and a PURPOSE step says how the customisation depends on the
 * asset - a design reference, or a runtime asset where the host embeds assets), 'text' (new rich-text block), 'edit'
 * (an existing asset of any kind: key while unused, description, active; a new file for image / pdf; the content for
 * text). The key is derived from the filename (or the description for text) and stays editable until a customisation
 * declares the asset — then the server refuses a rename and the field is locked here with the customisations listed.
 *
 * Resolves { asset, purpose? } (the saved model; the chosen purpose in mode 'any') or { canceled: true }.
 * `allowRuntimePurpose` (mode 'any') offers the runtime choice; `initialDescription` pre-fills the description.
 */
export const CustomizationAssetDlg = ({ mode = 'upload', asset = null, allowRuntimePurpose = false, initialDescription = '', onClose }) => {
  const isEdit = mode === 'edit' && !!asset?.ID;
  const withPurpose = mode === 'any';
  const kind = isEdit ? asset.Kind : (mode === 'text' ? 'text' : 'image'); // 'image' until a pdf / text file is chosen
  const isText = kind === 'text';

  const [key, setKey] = React.useState(asset?.Key ?? '');
  const [keyTouched, setKeyTouched] = React.useState(isEdit);
  const [description, setDescription] = React.useState(asset?.Description ?? initialDescription ?? '');
  const [active, setActive] = React.useState(asset?.Active ?? true);
  const [text, setText] = React.useState(asset?.Text ?? '');
  const [file, setFile] = React.useState(null);
  const [fileKind, setFileKind] = React.useState(kind);
  const [purpose, setPurpose] = React.useState(ASSET_PURPOSE_REFERENCE);
  const [preview, setPreview] = React.useState(null); // data / object URL of the chosen or stored image
  const [usedBy, setUsedBy] = React.useState(isEdit && asset.UsedByCount > 0 ? null : []);
  const [saving, setSaving] = React.useState(false);
  const [describing, setDescribing] = React.useState(false);
  const [error, setError] = React.useState('');
  const fileInputRef = React.useRef(null);
  const [keySeed, setKeySeed] = React.useState(0); // remounts the (uncontrolled) key field when a suggestion arrives
  const [descriptionSeed, setDescriptionSeed] = React.useState(0);

  const keyLocked = isEdit && (asset.UsedByCount ?? 0) > 0;

  // the templates that declare the asset (why the key is locked; what a delete would break)
  React.useEffect(() => {
    if (!isEdit || (asset.UsedByCount ?? 0) === 0) return;
    let cancelled = false;
    getCustomizationAssetUsedBy(asset.ID)
      .then((rows) => { if (!cancelled) setUsedBy(rows); })
      .catch(() => { if (!cancelled) setUsedBy([]); });
    return () => { cancelled = true; };
  }, [isEdit, asset]);

  // the stored thumbnail of an existing image / pdf
  React.useEffect(() => {
    if (!isEdit || !asset.HasThumbnail) return;
    let cancelled = false;
    fetchCustomizationAssetThumbnail(asset.ID, asset.ContentHash)
      .then((url) => { if (!cancelled && url) setPreview((p) => p ?? url); })
      .catch(() => { });
    return () => { cancelled = true; };
  }, [isEdit, asset]);

  const previewRef = React.useRef(null);
  React.useEffect(() => () => {
    if (previewRef.current) URL.revokeObjectURL(previewRef.current);
  }, []);

  const onFileChosen = async (e) => {
    const chosen = e?.target?.files?.[0] ?? null;
    if (!chosen) return;
    setError('');
    const chosenKind = kindForFileName(chosen.name, chosen.type);
    // a replacement must stay the same kind: customisations reference the asset expecting an image, a pdf or text
    if (isEdit && chosenKind !== asset.Kind) {
      setError(asset.Kind === 'image'
        ? t('This asset is an image; choose a PNG, JPEG or SVG to replace it (or upload the file as a new asset).')
        : asset.Kind === 'pdf'
          ? t('This asset is a PDF; choose a PDF to replace it (or upload the file as a new asset).')
          : asset.Kind === 'table'
            ? t('This asset is a spreadsheet; choose an .xlsx or .xls file to replace it (or upload the file as a new asset).')
            : t('This asset is text; choose a text file (.txt, .md, .json, .yaml, .csv) to replace it (or upload the file as a new asset).'));
      e.target.value = '';
      return;
    }
    setFile(chosen);
    setFileKind(chosenKind);

    // a local preview for images (the server renders the thumbnail of a pdf after the upload; text has none)
    if (previewRef.current) { URL.revokeObjectURL(previewRef.current); previewRef.current = null; }
    if (chosenKind === 'image') {
      const url = URL.createObjectURL(chosen);
      previewRef.current = url;
      setPreview(url);
    } else {
      setPreview(null);
    }

    // a new asset's key follows the filename until the user edits it; the server's suggestion is unique
    if (!isEdit && !keyTouched) {
      const local = deriveAssetKey(chosen.name);
      setKey(local);
      setKeySeed((k) => k + 1);
      try {
        const suggested = await suggestCustomizationAssetKey(chosen.name);
        if (suggested && suggested !== local) {
          setKey((current) => (current === local ? suggested : current));
          setKeySeed((k) => k + 1);
        }
      } catch {
        // the local slug stands; a clash is reported by the server on save
      }
    }
  };

  const onDescriptionChanged = (v) => {
    const value = v ?? '';
    setDescription(value);
    // a new text asset's key follows its description until edited by hand
    if (!isEdit && isText && !keyTouched) {
      setKey(deriveAssetKey(value));
      setKeySeed((k) => k + 1);
    }
  };

  const describe = async (e) => {
    try {
      setError('');
      setDescribing(true);
      const suggestion = await describeCustomizationAsset(asset.ID);
      if (isFrameworkError(suggestion)) return;
      if (typeof suggestion === 'string' && suggestion.trim()) {
        setDescription(suggestion.trim());
        setDescriptionSeed((k) => k + 1);
      }
    } catch (ex) {
      setError(ex?.message ?? t('The description could not be generated.'));
    } finally {
      setDescribing(false);
      e?.promise?.();
    }
  };

  const save = async (e) => {
    try {
      setError('');
      const trimmedKey = (key ?? '').toString().trim();
      const err = keyLocked ? null : keyError(trimmedKey);
      if (err) { setError(err); return; }
      if (!isEdit && !isText && !file) { setError(t('Choose a file to upload.')); return; }
      if (isText) {
        const plain = (text ?? '').toString();
        if (accentUtils.isEmpty(plain.replace(/<[^>]*>/g, '').trim())) { setError(t('Enter the text of the asset.')); return; }
        if (plain.length > MAX_TEXT_CHARS) { setError(t('The text is too long ({{length}} characters; the limit is {{max}}).', { length: plain.length, max: MAX_TEXT_CHARS })); return; }
      }

      setSaving(true);
      let saved;
      if (isText) {
        saved = await saveCustomizationAssetText({ id: isEdit ? asset.ID : null, key: keyLocked ? null : trimmedKey, description, text });
        if (isFrameworkError(saved)) return;
        if (isEdit && active !== asset.Active) {
          saved = await updateCustomizationAsset({ id: asset.ID, active });
          if (isFrameworkError(saved)) return;
        }
      } else if (!isEdit) {
        saved = await uploadCustomizationAsset({ key: trimmedKey, description: description || null, file });
        if (isFrameworkError(saved)) return;
      } else {
        // metadata first (a locked key is not sent), then the replacement file when one was chosen
        const metaChanged = (!keyLocked && trimmedKey !== asset.Key) || (description ?? '') !== (asset.Description ?? '') || active !== asset.Active;
        saved = asset;
        if (metaChanged) {
          saved = await updateCustomizationAsset({ id: asset.ID, key: keyLocked ? null : trimmedKey, description, active });
          if (isFrameworkError(saved)) return;
        }
        if (file) {
          saved = await uploadCustomizationAsset({ id: asset.ID, file });
          if (isFrameworkError(saved)) return;
        }
      }
      if (!saved || typeof saved !== 'object' || !saved.ID) {
        setError(t('The asset could not be saved.'));
        return;
      }
      onClose?.(withPurpose ? { asset: saved, purpose } : { asset: saved });
    } catch (ex) {
      setError(ex?.message ?? t('The asset could not be saved.'));
    } finally {
      setSaving(false);
      e?.promise?.();
    }
  };

  const title = isEdit
    ? `${t('Asset')} - ${asset.Key}`
    : (isText ? t('New Text Asset') : (withPurpose ? t('Add to the asset library') : t('Upload Asset')));

  const fileLabel = file
    ? `${file.name} (${formatAssetSize(file.size)})`
    : (isEdit && asset.FileName ? `${asset.FileName}${describeAssetMeasure(asset) ? ` · ${describeAssetMeasure(asset)}` : ''}` : '');

  return (
    <AccentDialog
      headerContent={title}
      onClose={onClose}
      relativeWidth={(d) => Math.min(isText ? 980 : 760, d.width - 40)}
      relativeHeight={(d) => Math.min(isText ? 760 : 640, d.height - 40)}
      actionsContent={[
        <AccentButton key="save" tagName="assetSave" disabled={saving} onClick={save}>
          {saving ? t('Saving...') : t('Save')}
        </AccentButton>,
      ]}
    >
      <div style={{ display: 'grid', gap: 12 }}>
        {!!error && <div className="alert alert-danger" style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>{error}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: isText ? '1fr' : '1fr 220px', gap: 16 }}>
          <div style={{ display: 'grid', gap: 10, alignContent: 'start' }}>
            {!isText && (
              <div>
                <input ref={fileInputRef} type="file" accept={ASSET_FILE_ACCEPT} style={{ display: 'none' }} onChange={onFileChosen} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <AccentButton size="sm" outline tagName="assetChooseFile" disabled={saving} onClick={(e) => { fileInputRef.current?.click(); e?.promise?.(); }}>
                    {isEdit ? t('Replace file...') : t('Choose file...')}
                  </AccentButton>
                  <span style={{ fontSize: 12, color: '#475569' }}>{fileLabel || t('PNG, JPEG or SVG up to 5 MB; PDF up to 10 MB and 50 pages; text files (.txt, .md, .json, .yaml, .csv) up to 64 KB.')}</span>
                </div>
                {isEdit && file && (
                  <div style={{ fontSize: 11, color: '#b45309', marginTop: 4 }}>
                    {t('The new file replaces the stored one on Save; customisations keep referencing the asset by its key. The kind (image / PDF / text) must stay the same.')}
                  </div>
                )}
              </div>
            )}

            {withPurpose && (
              <div data-tagname="assetPurpose" style={{ display: 'grid', gap: 4, padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#f8fafc' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>{t('How does this customisation depend on it?')}</div>
                <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, cursor: 'pointer' }}>
                  <input type="radio" name="assetPurpose" checked={purpose === ASSET_PURPOSE_REFERENCE} disabled={saving} onChange={() => setPurpose(ASSET_PURPOSE_REFERENCE)} style={{ marginTop: 2 }} />
                  <span>
                    <strong>{t('Design reference')}</strong>
                    {' - '}
                    {t('the assistant reads it when designing or changing the outputs (API documentation, a field list, a schema, a sample). It never appears in the output.')}
                  </span>
                </label>
                {allowRuntimePurpose && (
                  <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, cursor: 'pointer' }}>
                    <input type="radio" name="assetPurpose" checked={purpose === ASSET_PURPOSE_RUNTIME} disabled={saving} onChange={() => setPurpose(ASSET_PURPOSE_RUNTIME)} style={{ marginTop: 2 }} />
                    <span>
                      <strong>{t('Runtime asset')}</strong>
                      {' - '}
                      {t('the output embeds it when it runs (a logo, a letterhead, a terms block).')}
                    </span>
                  </label>
                )}
              </div>
            )}

            <AccentTextUnbound
              key={`key_${keySeed}`}
              label={t('Key')}
              required
              disabled={saving || keyLocked}
              defaultValue={key}
              maxLength={MAX_KEY_LENGTH}
              onChange={(v) => { setKey((v ?? '').toString().trim().toLowerCase()); setKeyTouched(true); }}
              dataTagName="assetKey"
            />
            <div style={{ fontSize: 11, color: '#64748b', marginTop: -6 }}>
              {keyLocked
                ? t('The key is locked because templates reference it (listed below). Rename is possible once no template declares the asset.')
                : t('How scripts reference the asset (asset:{{key}} for images). Lower-case letters, digits and hyphens.', { key: key || 'key' })}
            </div>

            <AccentTextAreaUnbound
              key={`description_${descriptionSeed}`}
              label={t('Description')}
              rows={3}
              max={500}
              disabled={saving || describing}
              defaultValue={description}
              onChange={onDescriptionChanged}
              dataTagName="assetDescription"
              overlayButtons={isEdit ? [
                describing
                  ? <AccentSpinner key="spin" />
                  : <AiWandButton key="wand" tagName="assetDescribe" size={16} title={t('Describe with AI')} onClick={describe} disabled={saving} />,
              ] : undefined}
            />
            <div style={{ fontSize: 11, color: '#64748b', marginTop: -6 }}>
              {isEdit
                ? t('Shown in the Assets picker and told to the assistant so it knows what the asset is for. The wand asks the AI to describe the content.')
                : t('Shown in the Assets picker and told to the assistant so it knows what the asset is for. After saving, the wand can describe the content for you.')}
            </div>

            {isEdit && (
              <AccentCheckUnbound label={t('Active')} defaultValue={active} disabled={saving} onChange={(e) => setActive(!!e.checked)} tagName="assetActive" />
            )}
            {isEdit && !active && (
              <div style={{ fontSize: 11, color: '#b45309', marginTop: -6 }}>
                {t('An inactive asset is hidden from the pickers and blocks publishing of any template that still declares it.')}
              </div>
            )}

            {isEdit && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>{t('Used by')}</div>
                {usedBy === null ? (
                  <div style={{ fontSize: 12, color: '#64748b' }}>{t('Loading...')}</div>
                ) : usedBy.length === 0 ? (
                  <div style={{ fontSize: 12, color: '#64748b' }}>{t('No customisation depends on this asset.')}</div>
                ) : (
                  <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontSize: 12 }}>
                    {usedBy.map((u) => (
                      <li key={u.CustomizationID}>
                        {u.Name}
                        <span style={{ color: '#64748b' }}>
                          {' '}({[
                            u.CustomizationType ? u.CustomizationType : null,
                            u.Purpose === ASSET_PURPOSE_RUNTIME ? t('runtime asset') : (u.Purpose ? t('design reference') : null),
                            u.Published ? t('published') : null,
                            u.Draft ? t('draft') : null,
                          ].filter(Boolean).join(', ')})
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          {!isText && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>{kindLabel(fileKind)}</div>
              <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, background: '#f8fafc', minHeight: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 8 }}>
                {preview ? (
                  <img src={preview} alt="" style={{ maxWidth: '100%', maxHeight: 220, objectFit: 'contain' }} />
                ) : (
                  <span style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
                    {fileKind === 'pdf'
                      ? (file ? t('The first page is rendered after the upload.') : t('No preview'))
                      : fileKind === 'text'
                        ? t('Stored as text; the assistant reads it as written.')
                        : fileKind === 'table'
                          ? t('Stored as uploaded; the assistant reads it sheet by sheet.')
                          : (isEdit && asset.ContentType === 'image/svg+xml' ? t('SVG assets have no thumbnail.') : t('No preview'))}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {isText && (
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>{t('Text')}</div>
            <AccentHtmlUnbound
              defaultValue={text || '<p></p>'}
              height={isEdit ? 300 : 320}
              onChange={(html) => setText(html ?? '')}
              dataTagName="assetText"
            />
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
              {t('Rendered with P.richText({ asset: \'{{key}}\' }) or read as ctx.assets.text[\'{{key}}\']. Headings, bold / italic, lists and tables are supported.', { key: key || 'key' })}
            </div>
          </div>
        )}
      </div>
    </AccentDialog>
  );
};
